import { useEffect, useRef, useState } from "react";
import { FiCheckCircle, FiFileText } from "react-icons/fi";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { Client } from "@/features/clients/types";
import { SendESignDialog } from "@/features/esignatures/components/send-esign-dialog";
import { generateSubmissionPdf, previewSubmissionPdf, saveSubmissionData } from "@/features/forms/api";
import {
  completeWorkflow,
  getOrCreateCaseSubmission,
  markCasePendingIrs,
} from "@/features/workflows/api";
import { applyTerms, normalizeTerms, type CommonTerms } from "@/features/workflows/terms";
import type { ResolutionWorkflow } from "@/features/workflows/types";

interface Props {
  client: Client;
  caseId: string;
  workflow: ResolutionWorkflow;
  onCompleted: (workflow: ResolutionWorkflow) => void;
}

const FORM_TITLES: Record<string, string> = {
  "9465": "Form 9465 — Installment Agreement Request",
  "433-H": "Form 433-H — IA Request + CIS",
  "433-D": "Form 433-D — Installment Agreement",
  "2159": "Form 2159 — Payroll Deduction Agreement",
};

/**
 * Step 8 — Generate & Attach. Pushes the step-7 terms onto each chosen output
 * form and offers Preview / Generate (→ Documents FORMS folder, attached to the
 * case). Complete marks the workflow done (writes a Case system-note) and
 * prompts — without silently changing — whether to move the case to Pending IRS.
 */
export function GenerateStep({ client, caseId, workflow, onCompleted }: Props) {
  const termsForm = workflow.selectedForm === "433-D" ? "433-D" : "433-H";
  const [terms, setTerms] = useState<CommonTerms | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [generated, setGenerated] = useState<Record<string, string>>({});
  // The most recently generated document, offered for e-signature.
  const [lastDoc, setLastDoc] = useState<{ id: string; name: string } | null>(null);
  const [esignOpen, setEsignOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    getOrCreateCaseSubmission(client.id, caseId, termsForm)
      .then((s) => mounted.current && setTerms(normalizeTerms(termsForm, s.data)))
      .catch(() => undefined);
    return () => {
      mounted.current = false;
    };
  }, [client.id, caseId, termsForm]);

  // 9465 always; the chosen 433; 2159 only when paying by payroll deduction.
  const outputForms = ["9465", termsForm, ...(terms?.payroll ? ["2159"] : [])].filter(
    (f, i, a) => a.indexOf(f) === i,
  );

  /** Ensure the target form's submission carries the entered terms. */
  async function prepare(formNumber: string): Promise<string> {
    const sub = await getOrCreateCaseSubmission(client.id, caseId, formNumber);
    if (terms) {
      await saveSubmissionData(sub.id, applyTerms(formNumber, terms), "DRAFT");
    }
    return sub.id;
  }

  async function handlePreview(formNumber: string) {
    setBusy(formNumber);
    const win = window.open("", "_blank");
    try {
      const id = await prepare(formNumber);
      await previewSubmissionPdf(id, win);
    } catch {
      win?.close();
      toast.error(`Couldn't preview ${formNumber}.`);
    } finally {
      if (mounted.current) setBusy(null);
    }
  }

  async function handleGenerate(formNumber: string) {
    setBusy(formNumber);
    try {
      const id = await prepare(formNumber);
      const updated = await generateSubmissionPdf(id);
      if (mounted.current) {
        setGenerated((g) => ({
          ...g,
          [formNumber]: updated.generatedDocument?.name ?? "Saved to Documents",
        }));
        if (updated.generatedDocument) {
          setLastDoc({
            id: updated.generatedDocument.id,
            name: updated.generatedDocument.name,
          });
        }
      }
      toast.success(`${formNumber} generated and saved to Documents.`);
    } catch {
      toast.error(`Couldn't generate ${formNumber}.`);
    } finally {
      if (mounted.current) setBusy(null);
    }
  }

  async function handleComplete() {
    setCompleting(true);
    try {
      const updated = await completeWorkflow(caseId, workflow.id);
      onCompleted(updated);
      toast.success("Workflow completed — a note was added to the case.");
      setPrompt(true);
    } catch {
      toast.error("Couldn't complete the workflow.");
    } finally {
      if (mounted.current) setCompleting(false);
    }
  }

  async function confirmPendingIrs() {
    try {
      await markCasePendingIrs(caseId);
      toast.success("Case moved to Pending IRS.");
    } catch {
      toast.error("Couldn't update the case status.");
    } finally {
      setPrompt(false);
    }
  }

  const isCompleted = workflow.status === "COMPLETED";

  return (
    <div className="grid gap-6">
      <Card className="gap-4 p-6">
        <h3 className="text-lg font-semibold">Output forms</h3>
        <p className="text-muted-foreground text-sm">
          The terms you entered are pushed onto each form before generating. Generated
          PDFs are saved to the client's Forms documents and attached to this case.
        </p>
        <div className="grid gap-3">
          {outputForms.map((f) => (
            <div
              key={f}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="flex items-center gap-2 text-sm">
                <FiFileText className="text-muted-foreground size-4" />
                <span className="font-medium">{FORM_TITLES[f] ?? f}</span>
                {generated[f] && (
                  <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                    <FiCheckCircle className="size-3.5 text-green-600" /> {generated[f]}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy !== null}
                  onClick={() => handlePreview(f)}
                >
                  Preview
                </Button>
                <Button size="sm" disabled={busy !== null} onClick={() => handleGenerate(f)}>
                  {busy === f ? "Generating…" : generated[f] ? "Regenerate" : "Generate"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toast.info("Add supplemental notes from the case's Notes tab.")}
          >
            Notes
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!lastDoc}
            title={lastDoc ? `Send ${lastDoc.name} for e-signature` : "Generate a form first"}
            onClick={() => setEsignOpen(true)}
          >
            Get e-signature
          </Button>
        </div>
        <Button onClick={handleComplete} disabled={completing || isCompleted}>
          <FiCheckCircle className="size-4" />
          {isCompleted ? "Workflow completed" : completing ? "Completing…" : "Complete workflow"}
        </Button>
      </Card>

      <AlertDialog open={prompt} onOpenChange={setPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark case as Pending IRS?</AlertDialogTitle>
            <AlertDialogDescription>
              The IA forms are filed. Move this case to “Pending IRS” to reflect that
              you're awaiting an IRS response? You can change it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingIrs}>
              Mark Pending IRS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {lastDoc && (
        <SendESignDialog
          open={esignOpen}
          onOpenChange={setEsignOpen}
          client={client}
          document={lastDoc}
        />
      )}
    </div>
  );
}
